import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Play, Pause, ChevronLeft, ChevronRight, Truck, Tag } from "lucide-react";
import GiantStat from "components/admin/GiantStat";
import AdminCard from "components/admin/AdminCard";
import { Button } from "components/ui/button";
import CustomAreaChart from "components/CustomAreaChart";
import WorldShipmentMap from "components/admin/WorldShipmentMap";
import { adminPaths } from "app/paths";
import {
  AdminDashboardPeriodSummary,
  getPrimeiraAtividade,
  getAdminDashboardPeriodSummary,
  getEnviosPorDia,
  getEnviosPorPais,
  PaisEnvios,
} from "services/admin/adminDashboard.service";
import { ChartPoint } from "services/admin/adminCommon.types";

const CHECKPOINT_DURATION_MS = 3500;

type Checkpoint = { year: number; month: number };

const toYMD = (d: Date) => d.toISOString().slice(0, 10);

const monthsBetween = (start: Date, end: Date): Checkpoint[] => {
  const months: Checkpoint[] = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cur <= last) {
    months.push({ year: cur.getFullYear(), month: cur.getMonth() });
    cur.setMonth(cur.getMonth() + 1);
  }
  return months;
};

const monthRange = (cp: Checkpoint) => {
  const from = new Date(cp.year, cp.month, 1);
  const to = new Date(cp.year, cp.month + 1, 0);
  return { date_from: toYMD(from), date_to: toYMD(to) };
};

const MONTH_LABEL = new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" });

const AdminMaquinaDoTempo = () => {
  const navigate = useNavigate();
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);

  const [periodSummary, setPeriodSummary] = useState<AdminDashboardPeriodSummary | null>(null);
  const [enviosPorDia, setEnviosPorDia] = useState<ChartPoint[]>([]);
  const [enviosPorPais, setEnviosPorPais] = useState<PaisEnvios[]>([]);

  useEffect(() => {
    getPrimeiraAtividade()
      .then((iso) => setCheckpoints(monthsBetween(new Date(iso), new Date())))
      .catch(() => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth() - 11, 1);
        setCheckpoints(monthsBetween(start, now));
      });
  }, []);

  useEffect(() => {
    if (!checkpoints.length) return;
    const range = monthRange(checkpoints[index]);
    getAdminDashboardPeriodSummary(range).then(setPeriodSummary).catch(() => {});
    getEnviosPorDia(range).then(setEnviosPorDia).catch(() => {});
    getEnviosPorPais(range).then(setEnviosPorPais).catch(() => {});
  }, [checkpoints, index]);

  useEffect(() => {
    if (!playing || checkpoints.length === 0) return;
    if (index >= checkpoints.length - 1) {
      setPlaying(false);
      return;
    }
    const t = setTimeout(() => setIndex((i) => Math.min(i + 1, checkpoints.length - 1)), CHECKPOINT_DURATION_MS);
    return () => clearTimeout(t);
  }, [playing, index, checkpoints.length]);

  const goTo = useCallback((i: number) => {
    setPlaying(false);
    setIndex(i);
  }, []);

  const togglePlay = () => {
    if (!playing && index >= checkpoints.length - 1) setIndex(0);
    setPlaying((p) => !p);
  };

  const exit = () => navigate(adminPaths.dashboard);

  const current = checkpoints[index];
  const label = current ? MONTH_LABEL.format(new Date(current.year, current.month, 1)) : "—";

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-brand-bg font-Inter text-brand-text">
      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-8 py-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">
          Intrex · Máquina do Tempo
        </p>
        <Button variant="outline" size="icon" onClick={exit}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto px-8 py-6">
        <h1 key={index} className="mb-6 text-center text-4xl font-bold capitalize tracking-tight text-slate-900">
          {label}
        </h1>

        <div className="w-full max-w-5xl">
          <div className="mb-5 grid grid-cols-2 gap-4">
            <GiantStat label="Envios no mês" value={periodSummary?.enviosNoPeriodo ?? "—"} icon={<Truck />} />
            <GiantStat
              label="Etiquetas emitidas"
              value={periodSummary?.etiquetasEmitidas ?? "—"}
              icon={<Tag />}
            />
          </div>

          <AdminCard title="Envios por dia" className="mb-5">
            <CustomAreaChart data={enviosPorDia} className="h-[260px]" />
          </AdminCard>

          <AdminCard title="Envios por país">
            <WorldShipmentMap data={enviosPorPais} />
          </AdminCard>
        </div>
      </div>

      <div className="flex flex-col items-center gap-3 border-t border-slate-200 bg-white px-8 py-6">
        <div className="flex w-full max-w-2xl items-center gap-4">
          <span className="w-16 shrink-0 text-xs text-slate-400">
            {checkpoints[0] ? MONTH_LABEL.format(new Date(checkpoints[0].year, checkpoints[0].month, 1)) : ""}
          </span>
          <input
            type="range"
            min={0}
            max={Math.max(checkpoints.length - 1, 0)}
            value={index}
            onChange={(e) => goTo(Number(e.target.value))}
            className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-slate-200 accent-brand-primary"
          />
          <span className="w-16 shrink-0 text-right text-xs text-slate-400">Hoje</span>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => goTo(Math.max(index - 1, 0))}
            disabled={index === 0}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={togglePlay}>
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => goTo(Math.min(index + 1, checkpoints.length - 1))}
            disabled={index >= checkpoints.length - 1}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminMaquinaDoTempo;
